import type { ComponentType } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router'
import type { ModuleId } from '@wp-one/auth'
import { RootError } from './RootError'
import { ProtectedModule } from './ProtectedModule'
import { PRIVATE_PATHS, PUBLIC_PATHS } from './publicPaths'

/** Loads a module on demand and wraps it in the profile access guard. */
function protectedModule(
  module: ModuleId,
  load: () => Promise<{ default: ComponentType }>,
  publicPaths: readonly string[] = [],
  privatePaths: readonly string[] = [],
) {
  return async () => {
    const { default: Module } = await load()
    return {
      Component: () => (
        <ProtectedModule module={module} publicPaths={publicPaths} privatePaths={privatePaths}>
          <Module />
        </ProtectedModule>
      ),
    }
  }
}

/**
 * Single app: each module is lazy-loaded on its reserved path.
 * Order matters: ONE owns the root and comes last.
 * Each module is a workspace package in front/modules/<name> (@wp-one/<name>).
 * Rule: a module never imports from another module (ESLint/boundaries).
 */
const routes: RouteObject[] = [
  // Conta UniFECAF is public (sign in/sign up); it protects its own inner pages.
  { path: 'conta/*', lazy: async () => ({ Component: (await import('@wp-one/conta')).default }) },
  {
    path: 'parcelas/*',
    lazy: protectedModule('emdia', () => import('@wp-one/emdia'), PUBLIC_PATHS.emdia),
  },
  {
    path: 'carreiras/*',
    lazy: protectedModule(
      'carreiras',
      () => import('@wp-one/carreiras'),
      PUBLIC_PATHS.carreiras,
      PRIVATE_PATHS.carreiras,
    ),
  },
  {
    path: 'conquistas/*',
    lazy: protectedModule(
      'conquistas',
      () => import('@wp-one/conquistas'),
      PUBLIC_PATHS.conquistas,
      PRIVATE_PATHS.conquistas,
    ),
  },
  {
    path: 'eventos/*',
    lazy: protectedModule(
      'eventos',
      () => import('@wp-one/eventos'),
      PUBLIC_PATHS.eventos,
      PRIVATE_PATHS.eventos,
    ),
  },
  // Vitrine do packages/ui: em todos os ambientes desde 15/10/2026 (pedido do Lucas, para
  // revisar os componentes em HML e PRD). Só exemplos fixos, sem dado real; carregada sob demanda.
  {
    path: 'vitrine/*',
    lazy: async () => ({ Component: (await import('../dev/Vitrine')).default }),
  },
  // Mapa de conteúdo do Feroz (packages/feroz) — mesmo papel do /vitrine, referência sem dado
  // real. Feroz não faz parte da entrega principal; ver front/CLAUDE.md.
  {
    path: 'feroz-mapa',
    lazy: async () => ({ Component: (await import('../dev/FerozMapa')).default }),
  },
  // DEV ONLY: shell sem topbar, usado só pelos testes de tela.
  ...(import.meta.env.DEV
    ? [
        {
          path: 'vitrine-sem-nav',
          lazy: async () => ({ Component: (await import('../dev/ShellWithoutNav')).default }),
        },
      ]
    : []),
  { path: '*', lazy: protectedModule('one', () => import('@wp-one/one')) },
]

export const router = createBrowserRouter([{ errorElement: <RootError />, children: routes }])
